import { createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { COMPONENT_LIST_INIT_SORT } from "../constants";
import { issuesApi } from "../report/services/issues.api";
import { generateOverrideId } from "../utils";
import { initializeState, reportCleared } from "./sdlcReportSlice";
import { restoreState } from "./utils";
export const initFilter = {};
export const initSort = {
    property: "grade",
    reverse: true,
};
export const initPagination = {
    currentPage: 1,
    itemsPerPage: 10,
};
const expandedRowPagination = {
    currentPage: 1,
    itemsPerPage: 5,
};
export const initialState = {
    filter: initFilter,
    sort: initSort,
    pagination: initPagination,
    expandedRows: {},
    focusedRow: null,
    filteredByLink: false,
};
export const issuesSlice = createSlice({
    name: "issues",
    initialState: initialState,
    reducers: {
        filterUpdated: (state, action) => {
            state.filter[action.payload.name] = cloneDeep(action.payload.filter);
            state.filteredByLink = false;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        filterDeleted: (state, action) => {
            delete state.filter[action.payload];
            state.filteredByLink = false;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        filterByLink: (state, action) => {
            state.filter = cloneDeep(action.payload);
            state.filteredByLink = true;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        resetFilter: (state) => {
            state.filter = cloneDeep(initFilter);
            state.filteredByLink = false;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        sortUpdated: (state, action) => {
            state.sort = action.payload;
            state.pagination.currentPage = 1;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        paginationUpdated: (state, action) => {
            state.pagination = action.payload;
            state.expandedRows = {};
            state.focusedRow = null;
        },
        focusedRowUpdated: (state, action) => {
            state.focusedRow = action.payload;
        },
        expandedRowsUpdated: (state, action) => {
            const { expand, id, overrideIds } = action.payload;
            if (expand) {
                state.expandedRows[id] = {};
                overrideIds.forEach((overrideId) => {
                    state.expandedRows[id][overrideId] = {
                        pagination: expandedRowPagination,
                        sortBy: COMPONENT_LIST_INIT_SORT,
                    };
                });
            }
            else {
                delete state.expandedRows[id];
            }
        },
        expandedRowPaginationUpdated: (state, action) => {
            var _a;
            const { id, overrideId, pagination } = action.payload;
            const expandedRowDetails = (_a = state.expandedRows[id]) === null || _a === void 0 ? void 0 : _a[overrideId];
            if (expandedRowDetails) {
                expandedRowDetails.pagination = pagination;
            }
            else {
                // Row won't exist if it was expanded by one of user click actions
                state.expandedRows[id] = {
                    [overrideId]: {
                        pagination: pagination,
                        sortBy: COMPONENT_LIST_INIT_SORT,
                    },
                };
            }
        },
        expandedRowSortUpdated: (state, action) => {
            var _a;
            const { id, overrideId, sort } = action.payload;
            const expandedRowDetails = (_a = state.expandedRows[id]) === null || _a === void 0 ? void 0 : _a[overrideId];
            if (expandedRowDetails) {
                expandedRowDetails.sortBy = sort;
                expandedRowDetails.pagination.currentPage = 1;
            }
            else {
                // Row won't exist if it was expanded by one of user click actions
                state.expandedRows[id] = {
                    [overrideId]: {
                        pagination: expandedRowPagination,
                        sortBy: sort,
                    },
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initializeState, (state, action) => {
            return restoreState(state, action.payload, "issues");
        });
        builder.addCase(reportCleared, () => {
            return cloneDeep(initialState);
        });
        builder.addMatcher(issuesApi.endpoints.getIssues.matchFulfilled, (state, action) => {
            if (!state.filteredByLink || action.payload.match_count !== 1)
                return;
            const issue = action.payload.issues[0];
            const overrideIds = issue.overrides.map((override) => generateOverrideId(override));
            state.expandedRows[issue.id] = {};
            overrideIds.forEach((overrideId) => {
                state.expandedRows[issue.id][overrideId] = {
                    pagination: expandedRowPagination,
                    sortBy: COMPONENT_LIST_INIT_SORT,
                };
            });
            state.focusedRow = issue.id;
        });
    },
});
export const { filterUpdated, filterDeleted, filterByLink, resetFilter, sortUpdated, paginationUpdated, expandedRowsUpdated, focusedRowUpdated, expandedRowPaginationUpdated, expandedRowSortUpdated, } = issuesSlice.actions;
export const selectFilter = (state) => state.report.issues.filter;
export const selectSort = (state) => state.report.issues.sort;
export const selectPagination = (state) => state.report.issues.pagination;
export const selectStatusFilter = (state) => {
    if (state.report.issues.filter.status) {
        if (state.report.issues.filter.status.values.length === 1) {
            return state.report.issues.filter.status
                .values[0];
        }
    }
    return undefined;
};
export const selectExpandedRow = (state, id) => Object.keys(state.report.issues.expandedRows).some((key) => key.startsWith(id));
export const selectExpandedRowPagination = (state, id, overrideId) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = state.report.issues.expandedRows[id]) === null || _a === void 0 ? void 0 : _a[overrideId]) === null || _b === void 0 ? void 0 : _b.pagination) !== null && _c !== void 0 ? _c : expandedRowPagination;
};
export const selectExpandedRowSort = (state, id, overrideId) => {
    var _a, _b, _c;
    return (_c = (_b = (_a = state.report.issues.expandedRows[id]) === null || _a === void 0 ? void 0 : _a[overrideId]) === null || _b === void 0 ? void 0 : _b.sortBy) !== null && _c !== void 0 ? _c : COMPONENT_LIST_INIT_SORT;
};
export const selectFocusedRow = (state) => state.report.issues.focusedRow;
export const selectFilteredByLink = (state) => state.report.issues.filteredByLink;
export const issuesReducer = issuesSlice.reducer;
export * as default from "./issuesSlice";
//# sourceMappingURL=issuesSlice.js.map